/**
 * Form utilities and constants shared across all forms
 * 
 * Uses hybrid postal code lookup:
 * 1. Local database for multi-city postal codes
 * 2. Zippopotam.us API for all other codes (60+ countries)
 * 3. Nominatim fallback
 */
import { 
  lookupPostalCode, 
  lookupPostalCodeAPI, 
  lookupPostalCodeNominatim,
  getCountryCode,
  getAreaFromPostalCode,
  getProvinceFromPostalCode
} from '../../data/postalCodes';

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service Types (matching mobile app)
export const SERVICE_TYPES = [
  { key: 'tantric_massage', label: { es: 'Masaje Tántrico', en: 'Tantric Massage' }, icon: '✋' },
  { key: 'tantra_coaching', label: { es: 'Coaching Tántrico', en: 'Tantra Coaching' }, icon: '💬' },
  { key: 'couple_session', label: { es: 'Sesión de Pareja', en: 'Couple Session' }, icon: '❤️' },
  { key: 'tantra_therapy', label: { es: 'Terapia Tántrica', en: 'Tantra Therapy' }, icon: '🏥' },
  { key: 'tantra_initiation', label: { es: 'Iniciación Tántrica', en: 'Tantra Initiation' }, icon: '✨' },
  { key: 'esta_training', label: { es: 'ESTA - Entrenamiento en Sexualidad Tántrica', en: 'ESTA - Tantric Sexuality Training' }, icon: '🔥' },
];

// Course Types (matching mobile app)
export const COURSE_TYPES = [
  { key: 'workshop', label: { es: 'Taller', en: 'Workshop', it: 'Workshop' }, icon: 'Wrench', description: { es: 'Sesión intensiva corta', en: 'Short intensive session', it: 'Sessione intensiva breve' } },
  { key: 'retreat', label: { es: 'Retiro', en: 'Retreat', it: 'Ritiro' }, icon: 'Leaf', description: { es: 'Experiencia inmersiva de varios días', en: 'Multi-day immersive experience', it: 'Esperienza immersiva di più giorni' } },
  { key: 'training', label: { es: 'Formación', en: 'Training', it: 'Formazione' }, icon: 'GraduationCap', description: { es: 'Percurso certificado con varios módulos', en: 'Certified path with multiple modules', it: 'Percorso certificato con più moduli' } },
  { key: 'festival', label: { es: 'Festival', en: 'Festival', it: 'Festival' }, icon: 'Sparkles', description: { es: 'Evento con múltiples actividades y artistas', en: 'Event with multiple activities and artists', it: 'Evento con molteplici attività e artisti' } },
];

// Target Audience options
export const TARGET_AUDIENCE = [
  { key: 'all', label: { es: 'Todos', en: 'Everyone' } },
  { key: 'women', label: { es: 'Mujeres', en: 'Women' } },
  { key: 'men', label: { es: 'Hombres', en: 'Men' } },
  { key: 'couples', label: { es: 'Parejas', en: 'Couples' } },
  { key: 'singles', label: { es: 'Singles', en: 'Singles' } },
  { key: 'lgbtqia', label: { es: 'LGBTQIA+', en: 'LGBTQIA+' } },
];

/**
 * Hybrid postal code lookup with geolocation
 * 
 * Flow:
 * 1. Check local database for multi-city postal codes
 * 2. If not found, call Zippopotam.us API
 * 3. Fallback to Nominatim if API fails
 * 
 * @param {string} postalCode - The postal code to lookup
 * @param {string} country - Country name (default: España)
 * @returns {Promise<Object>} Location data with city, province, area, lat, lng
 */
export async function lookupPostalCodeWithGeo(postalCode, country = 'España') {
  // Normalize postal code
  const normalizedCode = postalCode.trim();
  if (!normalizedCode) {
    return { found: false, error: 'empty_code' };
  }
  
  // Get country code for API
  const countryCode = getCountryCode(country);
  
  // Step 1: Check local database (multi-city codes)
  const localResult = lookupPostalCode(normalizedCode);
  if (localResult.found) {
    // Multi-city postal code - return for user selection
    if (localResult.hasMultipleCities) {
      return {
        found: true,
        hasMultipleCities: true,
        cities: localResult.cities,
        province: localResult.province || getProvinceFromPostalCode(normalizedCode),
        area: localResult.area || getAreaFromPostalCode(normalizedCode),
        source: 'local'
      };
    }
    // Single city from local database
    return {
      found: true,
      city: localResult.city,
      province: localResult.province || getProvinceFromPostalCode(normalizedCode),
      area: localResult.area || getAreaFromPostalCode(normalizedCode),
      lat: localResult.lat,
      lng: localResult.lng,
      source: 'local'
    };
  }
  
  // Step 2: Try Zippopotam.us API
  const apiResult = await lookupPostalCodeAPI(normalizedCode, countryCode);
  if (apiResult.found) {
    // Enhance with Spanish province/area data if applicable
    if (countryCode === 'es') {
      apiResult.province = apiResult.province || getProvinceFromPostalCode(normalizedCode);
      apiResult.area = apiResult.area || getAreaFromPostalCode(normalizedCode);
    }
    return apiResult;
  }
  
  // Step 3: Fallback to Nominatim
  const countryNameMap = {
    'es': 'Spain', 'it': 'Italy', 'fr': 'France', 'de': 'Germany',
    'pt': 'Portugal', 'gb': 'United Kingdom', 'nl': 'Netherlands',
    'be': 'Belgium', 'ch': 'Switzerland', 'at': 'Austria',
    'us': 'United States', 'ca': 'Canada', 'mx': 'Mexico',
    'br': 'Brazil', 'ar': 'Argentina', 'au': 'Australia'
  };
  const countryName = countryNameMap[countryCode] || country;
  
  const nominatimResult = await lookupPostalCodeNominatim(normalizedCode, countryName);
  if (nominatimResult.found) {
    return nominatimResult;
  }
  
  // Not found anywhere
  return {
    found: false,
    city: '',
    province: getProvinceFromPostalCode(normalizedCode),
    area: getAreaFromPostalCode(normalizedCode),
    lat: 0,
    lng: 0,
    error: 'not_found'
  };
}

/**
 * Lookup city and get postal code (simplified - mainly for Spain)
 * Note: Full city lookup would require a larger database or API
 */
export function lookupCityToPostalCode(cityName) {
  // Simplified - return not found
  // In the future, could use an API for reverse lookup
  return { found: false };
}

/**
 * Check if country is Spain (multiple variants)
 */
export function isSpainCountry(country) {
  return ['españa', 'spain', 'spagna', 'espanha', 'spanien', 'es'].includes(
    country.toLowerCase().trim()
  );
}

// Social links platforms
export const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/tu_perfil' },
  { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/tu_perfil' },
  { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/@tu_canal' },
  { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@tu_perfil' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/tu_perfil' },
  { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/tu_perfil' },
  { key: 'telegram', label: 'Telegram', placeholder: 'https://t.me/tu_perfil' },
];

// Initial social links state
export const initialSocialLinks = {
  instagram: '',
  facebook: '',
  youtube: '',
  tiktok: '',
  linkedin: '',
  twitter: '',
  telegram: ''
};
